<template>
  <!-- Rebuilt on the KarroFin components. Same endpoint, same params, same
       edit-status dialog -- only the presentation moved. This was the last
       Operations screen still on the pre-KarroFin skin; Insurance,
       Disbursement, Team and Reports came across before it. -->
  <div class="kf-page">
    <!-- Status IS the filter, so it reads as one: press a cell to see only
         those leads, press it again (or "All") to clear. It replaces a "Any
         status" dropdown that hid the current selection inside a closed select,
         and it replaces the Status column, which repeated the same word on
         every row of a table that was already 124px too wide.

         Always drawn, the same as User Management and Offer Management. When
         neither the status list nor the counts have arrived the cells come from
         FALLBACK_STATUSES with their values unknown (see statusItems), so a
         failed request never collapses the strip to a lone "All" -- or, as it
         used to, takes the whole strip off the screen. -->
    <!-- Matched rows are leads (an offer the borrower applied with);
         unmatched rows are what a lender reported in an MIS file that reached
         no offer -- MIS-<id> report leads and CC<id> tracked clicks. A
         dropdown rather than a third strip, so the page reads as one status
         strip. The amount beside it is money lenders say they disbursed on the
         unmatched rows: reported, not recorded, so it is never part of any
         disbursal total. Clicking it shows those rows. -->
    <div class="kf-match-row">
      <v-select
        v-model="match_state"
        :items="matchOptions"
        class="kf-match-row__select"
        dense
        outlined
        hide-details
        @change="reload()"
      />
      <button
        type="button"
        class="kf-match-row__amount"
        title="Show the unmatched rows"
        @click="showUnmatched"
      >
        Lender-reported disbursals, unmatched:
        <strong>{{ reportedAmountText }}</strong>
      </button>
    </div>

    <StatStrip
      :items="statusItems"
      :active="status || null"
      :loading="table_loading"
      @select="selectStatus"
    />

    <!--
      Mixed on purpose. The six UTM filters and the Disbursement date live in
      one `filters` object, so they are described by `filterSchema` and drawn
      by the bar; the bar's own date box is Applied on. Loan type and
      Product do not -- they are component data, and Product is an async
      autocomplete that loads its list on click -- so they stay hand-written in
      the default slot, which renders ahead of the schema fields.
    -->
    <FilterBar
      :filters="filterSchema"
      :value="filters"
      :search.sync="search_query"
      :date-range.sync="date_range"
      date-title="Applied on"
      search-placeholder="Search by name, mobile, email, lead ID, MIS ref or lender lead ID"
      :advanced-count="appliedFilterCount"
      :result-count="total_count"
      result-label="leads"
      @change="reload()"
      @reset="resetFilters"
    >
      <div class="kf-field">
        <span class="kf-field__label">Loan type</span>
        <v-select
          v-model="loan_type"
          :items="loan_type_list"
          item-text="loan_type"
          item-value="loan_type"
          placeholder="Any loan type"
          dense
          outlined
          single-line
          hide-details
          clearable
          @change="reload()"
        />
      </div>

      <!-- Product loads its list on click and filters it server-side as you
           type, which is why it stays a hand-written autocomplete rather than a
           schema `select`: the schema describes options it is handed, not ones
           fetched on demand. -->
      <div class="kf-field">
        <span class="kf-field__label">Product</span>
        <v-autocomplete
          v-model="product"
          :items="product_list"
          :search-input.sync="product_search"
          item-text="name"
          item-value="id"
          placeholder="Any product"
          dense
          outlined
          single-line
          hide-details
          clearable
          hide-selected
          @change="reload()"
          @click="getProductList()"
        />
      </div>

    </FilterBar>

    <DataTable
      class="kf-leads-table"
      title="Leads"
      :headers="headers"
      :items="leads_list"
      :loading="table_loading"
      :page="page_number"
      :total-pages="total_page_count"
      :total-count="total_count"
      :page-length="page_length"
      item-key="row_key"
      empty-title="No leads match these filters"
      empty-message="Try widening a date range or clearing the search."
      @page-change="onPage"
      @retry="reload()"
    >
      <!-- A lead shows its offer id, plus the MIS-<id> references a lender
           reported it under before it was matched. An unmatched row has no
           offer: it shows its own reference instead. -->
      <template #[`item.lead_id`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.reference">
            {{ item.lead_id || item.reference }}
          </div>
          <div v-if="item.row_type && item.row_type !== 'lead'" class="kf-stack__sub">
            <span class="kf-tag kf-tag--unmatched">Unmatched</span>
          </div>
          <div
            v-else-if="item.mis_references && item.mis_references.length"
            class="kf-stack__sub kf-truncate"
            :title="item.mis_references.join(', ')"
          >
            {{ item.mis_references.join(", ") }}
          </div>
          <div v-if="item.repeat_of" class="kf-stack__sub">
            <span
              class="kf-tag kf-tag--repeat"
              :title="`Applied again for the same product within an hour of ${item.repeat_of}`"
            >Repeat of {{ item.repeat_of }}</span>
          </div>
        </div>
      </template>

      <template #[`item.customer_name`]="{ item }">
        <!-- An unmatched row has no customer record to name; what ops need is
             whether the mobile has an account yet and why it is not matched. -->
        <div v-if="item.row_type && item.row_type !== 'lead'" class="kf-stack">
          <div class="kf-truncate">
            {{ item.registration_state === "registered" ? "Registered" : "Not registered" }}
          </div>
          <div class="kf-stack__sub kf-truncate" :title="item.unmatched_reason_label">
            {{ item.unmatched_reason_label }}
            <template v-if="item.possible_duplicate_of"> · dup? {{ item.possible_duplicate_of }}</template>
          </div>
        </div>
        <div v-else class="kf-stack">
          <div class="kf-truncate" :title="item.customer_name">
            {{ fmt.orDash(item.customer_name) }}
          </div>
          <!-- Who owns the lead. The endpoint has always sent it; the old table
               had nowhere to put it. -->
          <div v-if="item.agent" class="kf-stack__sub kf-truncate" :title="item.agent">
            {{ item.agent }}
          </div>
        </div>
      </template>

      <template #[`item.mobile`]="{ item }">
        {{ fmt.mobile(item.mobile) }}
      </template>

      <template #[`item.email`]="{ item }">
        <span class="kf-truncate d-inline-block" :title="item.email">
          {{ fmt.orDash(item.email) }}
        </span>
      </template>

      <template #[`item.product`]="{ item }">
        <span class="kf-truncate d-inline-block" :title="item.product">
          {{ fmt.orDash(item.product) }}
        </span>
      </template>

      <!-- The time of day is the thing that distinguishes four leads applied on
           the same day, so it stays -- on the tooltip, where it does not cost a
           column 60px. -->
      <template #[`item.applied`]="{ item }">
        <span :title="item.applied">{{ fmt.date(item.applied) }}</span>
      </template>

      <template #[`item.status`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.status">{{ fmt.orDash(item.status) }}</div>
          <div v-if="item.sub_status" class="kf-stack__sub kf-truncate" :title="item.sub_status">
            {{ item.sub_status }}
          </div>
        </div>
      </template>

      <!-- On a lead this is the offer's recorded disbursal; on an unmatched row
           it is only what the lender reported, so it says so. -->
      <template #[`item.disbursal`]="{ item }">
        <div v-if="item.disbursal_amount" class="kf-stack">
          <div>{{ fmt.currency(item.disbursal_amount) }}</div>
          <div class="kf-stack__sub">
            {{ item.disbursal_date ? fmt.date(item.disbursal_date) : "" }}
            <template v-if="item.row_type && item.row_type !== 'lead'"> · reported</template>
          </div>
        </div>
        <span v-else>-</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="kf-actions">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <!-- Wrapped: a disabled button gets no hover, so the tooltip that
                   says why would never show. -->
              <span v-bind="attrs" v-on="on">
                <v-btn icon small :disabled="!item.lead_id" @click="editLoanStatus(item)">
                  <LucideIcon name="pencil" :size="17" />
                </v-btn>
              </span>
            </template>
            <span>{{ item.lead_id ? "Edit loan status" : "No offer yet -- match it first" }}</span>
          </v-tooltip>
          <v-tooltip v-if="item.row_type !== 'lead' || (item.mis_references && item.mis_references.length)" bottom>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click="openHistory(item)">
                <LucideIcon name="clock" :size="16" />
              </v-btn>
            </template>
            <span>Lender report history</span>
          </v-tooltip>
          <v-tooltip v-if="item.can_match && canMatch" bottom>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click="openMatch(item)">
                <LucideIcon name="link" :size="16" />
              </v-btn>
            </template>
            <span>Match to an offer</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                :disabled="!item.utm_link"
                @click="copyOfferLink(item.utm_link)"
              >
                <LucideIcon name="copy" :size="16" />
              </v-btn>
            </template>
            <span>Copy UTM link</span>
          </v-tooltip>
        </div>
      </template>
      <!-- The rows are the raw response here -- no mapper -- so the utm_*
           fields sit directly on the item. -->
      <template #[`item.utm`]="{ item }">
        <span v-if="item.row_type && item.row_type !== 'lead'">-</span>
        <a v-else class="kf-utm" @click.stop="ViewUtm(item)">
          {{ item.utm_source || "View" }}
        </a>
      </template>
    </DataTable>

    <!-- The legacy screens' dialog, unchanged: no props, reads the six utm_*
         keys off whatever object openDialog is handed. -->
    <ViewUTMDetail ref="UtmDialog" />

    <editLoanStatus ref="editLoanStatusDialog" @reload="get_lead_list()" />

    <ReportHistoryDialog v-model="historyOpen" :row="historyRow" />
    <ReportLeadMatchDialog v-model="matchOpen" :row="matchRow" @matched="onMatched" />
  </div>
</template>

<script>
import {
  getDefaultSignupDateRangeIso,
  localTodayYmd,
} from "@/utils/defaultSignupDateRange";
import debounce from "lodash/debounce";
import fmt from "@/app-js/karrofin/format";
import copyToClipboard from "@/app-js/karrofin/clipboard";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import StatStrip from "@/components/karrofin/StatStrip.vue";
import ReportHistoryDialog from "@/components/karrofin/ReportHistoryDialog.vue";
import ReportLeadMatchDialog from "@/components/karrofin/ReportLeadMatchDialog.vue";
import editLoanStatus from "../../components/pre-verification/dialog/editLoanStatus.vue";
import { UTM_FILTERS, utmParams } from "@/app-js/utm-filters";

/* Statuses kept off the filter strip. Credit Card is no longer sold, so these
   two are permanently-zero cells taking width from the six that mean something.
   Filtered here rather than server-side: GET_OFFER_STATUS_LIST also fills the
   Edit loan status dropdown (editLoanStatus.vue), which still needs them to
   read back an old card record. */
const HIDDEN_STATUSES = ["Card Dispatched", "Card Activated"];

/* The last resort for the strip's cells: the labels GET_OFFER_STATUS_LIST
   returns today, in its order. Read only when the server's list AND the lead
   list's counts have both failed to arrive -- an API restart, a 5xx, a timeout
   -- which otherwise left nothing to draw a cell from. A status added
   server-side still appears on its own the moment either request succeeds;
   this only has to be close enough to press until then. */
const FALLBACK_STATUSES = [
  "Application Pending",
  "Action Required",
  "Approved",
  "Disbursed",
  "Completed",
  "Rejected",
  "Card Dispatched",
  "Card Activated",
];

/* What a cell reads when its count has not arrived -- a dash, not a 0, so a
   failed request does not look like a screen with no leads. */
const UNKNOWN = "—";


export default {
  name: "Leads",
  components: {
    FilterBar,
    DataTable,
    StatStrip,
    ReportHistoryDialog,
    ReportLeadMatchDialog,
    ViewUTMDetail: () => import("@/components/las/ViewUTMDetail.vue"),
    editLoanStatus,
  },
  data() {
    return {
      fmt,
      debouncedLeadSearch: null,
      product_search: "",
      product: "",
      product_list: [],
      leads_list: [],
      table_loading: false,
      page_number: 1,
      total_page_count: 0,
      // The endpoint has always returned this alongside total_page_count; the
      // old success handler dropped it, which is why the table had no count.
      total_count: null,
      page_length: 10,
      status_list: [],
      // { <status label>: count, all: <total> } -- the strip's numbers, sent
      // down with the rows so the screen still costs one request.
      status_counts: {},
      status: "",
      // all | matched | unmatched -- see the first strip in the template
      match_state: "all",
      // { all, matched, unmatched } and { count, amount } from LeadsListView
      match_counts: {},
      unresolved_reported: {},
      historyOpen: false,
      historyRow: null,
      matchOpen: false,
      matchRow: null,
      loan_type: "",
      loan_type_list: [],
      date_range: getDefaultSignupDateRangeIso(),
      // What is selected. Arrays throughout -- MultiSelect's clear button is
      // what would otherwise write null here, and axios drops a null param
      // silently, so resetting to [] keeps "nothing selected" one shape.
      filters: {
        utm_source: [], utm_medium: [], utm_campaign: [],
        utm_content: [], utm_term: [], utm_platform: [],
        // The Disbursal column's date, beside Applied on (date_range above).
        // Empty by default: most leads have not been disbursed, and a set
        // range hides every one that has no disbursal date.
        disbursal_range: [],
      },
      // What can be selected, from users/get_utm_dropdown/. Empty until it
      // lands, so a slow lookup shows an empty dropdown rather than breaking.
      utm_list: {
        utm_sources: [], utm_mediums: [], utm_campaigns: [],
        utm_contents: [], utm_terms: [], utm_platforms: [],
      },
      // Owned by the page now rather than read from the shared navbar search.
      // That store is written by Navbar.vue and cleared by Drawer.vue on every
      // navigation, which makes a screen that reads it behave unpredictably --
      // the same reason Reports moved its search into the filter bar.
      search_query: "",
      // Bumped per get_lead_list call; only the latest call may write the
      // table or clear the loading bar. See get_lead_list.
      lead_request_seq: 0,
      // Widths sum to 1132px, which is what the table area actually gets on a
      // 1366px laptop: the viewport minus the 200px drawer, the page gutter and
      // the card padding. The previous note claimed 1266 clearing "~1366 on a
      // 1600px screen" and was wrong twice over -- the real sum was 1256 inside
      // a 1132px container, so Status hung off the right edge and its chips were
      // clipped. Status is gone now and its 176px went to Product and Email,
      // the two that were truncating. See the table-layout:fixed rule below,
      // which is what makes these numbers binding rather than advisory.
      headers: [
        { text: "Lead ID", value: "lead_id", width: 112 },
        { text: "Name", value: "customer_name", width: 186 },
        { text: "Mobile", value: "mobile", width: 128 },
        { text: "Email", value: "email", width: 186 },
        { text: "Product", value: "product", width: 210 },
        { text: "Loan Type", value: "type", width: 126 },
        { text: "Applied On", value: "applied", width: 110 },
        // What the lender last said, and the disbursal: on a lead, the
        // offer's own; on an unmatched row, only what the lender reported.
        { text: "Lender status", value: "status", width: 150 },
        { text: "Disbursal", value: "disbursal", width: 120 },
        // Where the lead came from. The cell shows utm_source so the column
        // answers something on its own; the rest is behind the popup.
        { text: "UTM", value: "utm", width: 96 },
        { text: "", value: "actions", width: 130, align: "end" },
      ],
    };
  },
  created() {
    this.debouncedLeadSearch = debounce(() => {
      this.getProductList();
    }, 500);
  },
  mounted() {
    this.get_lead_list();
    this.getUtmList();
    this.getLoanType();
    this.getLoanStatusList();
    this.getProductList();
  },
  beforeDestroy() {
    this.debouncedLeadSearch.cancel();
  },
  watch: {
    // No search_query watcher: FilterBar emits `change` right after
    // `update:search`, and @change already reloads. Watching it too sent every
    // search twice (and Reset twice) -- see get_lead_list for why extra
    // in-flight requests were not harmless.
    //
    // Fetches the PRODUCT options, not the leads. It deliberately does not
    // touch page_number: it used to reset it without reloading, which left the
    // pager saying 1 while the table still showed page N.
    product_search(val) {
      if (!val || val.length < 2) return;
      this.debouncedLeadSearch();
    },
  },
  computed: {
    /** Ops may match a lender-reported lead to an offer ("MIS Match" grant). */
    canMatch() {
      return this.getPermission("MIS Match");
    },
    /**
     * The dropdown's three choices, each with its count once the first reply
     * is in. Before that the plain words, so the select never shows a dash.
     */
    matchOptions() {
      const counts = this.match_counts || {};
      const counted = typeof counts.all === "number";
      const option = (text, value) => ({
        text: counted ? `${text} (${fmt.number(counts[value] || 0)})` : text,
        value,
      });
      return [
        option("All leads", "all"),
        option("Matched", "matched"),
        option("Unmatched", "unmatched"),
      ];
    },
    reportedAmountText() {
      const counts = this.match_counts || {};
      if (typeof counts.all !== "number") return UNKNOWN;
      return fmt.currency((this.unresolved_reported || {}).amount || 0);
    },
    /**
     * The disbursement date and the six attribution filters, described rather
     * than hand-written. They are the ones this screen keeps in a single
     * `filters` object, which is what the bar needs to write back into; Loan
     * type and Product are component data and stay in the default slot.
     *
     * The spec key IS the singular of its option list, so the list name is
     * derived rather than repeated six times.
     */
    filterSchema() {
      return [
        // Pinned beside the bar's own date box, which is Applied on: the
        // Disbursement screen offers the same two ranges.
        {
          key: "disbursal_range",
          label: "Disbursement date",
          type: "date-range",
          pinned: true,
          max: localTodayYmd(),
        },
        ...UTM_FILTERS.map((f) => ({
          key: f.key,
          label: f.label,
          type: "multiselect",
          options: this.utm_list[f.key + "s"] || [],
        })),
      ];
    },
    /** How many filters are actually applied -- the pill beside "Filters". */
    appliedFilterCount() {
      const utm = UTM_FILTERS.filter(
        (f) => this.filters[f.key] && this.filters[f.key].length
      ).length;
      const disbursal = (this.filters.disbursal_range || []).length === 2 ? 1 : 0;
      return (this.product ? 1 : 0) + utm + disbursal;
    },
    /**
     * "All" plus one cell per status, in StatStrip's shape.
     *
     * Built from `status_list` -- the server's own OFFER_LOAN_STATUS choices,
     * already fetched in mounted() -- rather than from a hardcoded list here, so
     * a status added server-side appears without a frontend change. `filter` is
     * what goes on the wire and `label` is what is read; for this endpoint they
     * are the same string, which is also what the old dropdown sent.
     *
     * `status_counts` is keyed by that same label. A status missing from a
     * reply that did arrive reads 0. Before any counts have arrived at all --
     * no numeric `all` yet -- every cell reads a dash instead, so a failed
     * request is not shown as zero leads.
     *
     * "All" carries the unfiltered total, NOT total_count: total_count is the
     * current query's row count, so it would collapse to the selected status
     * the moment one was pressed. The server counts before applying `status`
     * for exactly this reason -- see LeadsListView.
     *
     * It keeps counting the HIDDEN_STATUSES rows too. Dropping a cell hides a
     * filter nobody can use any more; it does not hide the leads, which are
     * still in the table under All and still render their own status chip.
     * Subtracting them here would make the number disagree with the rows.
     */
    statusItems() {
      const counts = this.status_counts || {};
      /* Which statuses to offer. The server's own list normally, fetched once in
         mounted() -- but if THAT request is the one that failed there is still a
         source of truth on the page: `status_counts` is keyed by the same
         labels and rides along with the lead list. Without this fallback a
         failed status list left a single "All" cell, and the guard on the
         template then took the whole strip off the screen, refresh after
         refresh. Only when BOTH have failed does FALLBACK_STATUSES step in. */
      const fromCounts = Object.keys(counts).filter((key) => key !== "all");
      const labels = (this.status_list || []).length
        ? this.status_list.map((s) => s.label)
        : fromCounts.length
          ? fromCounts
          : FALLBACK_STATUSES;
      const counted = typeof counts.all === "number";
      const all = {
        label: "All",
        value: counted ? counts.all : UNKNOWN,
        isText: !counted,
        filter: null,
      };
      return [
        all,
        ...labels
          .filter((label) => HIDDEN_STATUSES.indexOf(label) === -1)
          .map((label) => ({
            label,
            value: counted ? counts[label] || 0 : UNKNOWN,
            isText: !counted,
            filter: label,
          })),
      ];
    },
  },
  methods: {
    /**
     * A status cell was pressed. StatStrip hands back null when the active one
     * is pressed again, which is the "clear it" case -- the param is sent as an
     * empty string because that is what the endpoint treats as no filter.
     */
    selectStatus(value) {
      this.status = value || "";
      this.reload();
    },
    showUnmatched() {
      if (this.match_state === "unmatched") return;
      this.match_state = "unmatched";
      this.reload();
    },
    openHistory(item) {
      this.historyRow = item;
      this.historyOpen = true;
    },
    openMatch(item) {
      this.matchRow = item;
      this.matchOpen = true;
    },
    onMatched(result) {
      this.showSnakeBar(
        "success",
        result && result.status_applied === false
          ? `${result.reference} matched; the offer already had a newer status, so it was kept`
          : `${(result && result.reference) || "Lead"} matched to offer ${result && result.offer_id}`
      );
      this.get_lead_list();
    },
    /* A filter changed. Page 1, because the row that was on page 3 under the
       old filter is not the row that is there under the new one -- the old code
       re-queried the current page and could land you on an empty table. */
    reload() {
      this.page_number = 1;
      this.get_lead_list();
    },
    onPage(page) {
      this.page_number = page;
      this.get_lead_list();
    },
    resetFilters() {
      this.status = "";
      this.match_state = "all";
      this.loan_type = "";
      this.product = "";
      this.product_search = "";
      this.date_range = getDefaultSignupDateRangeIso();
      this.search_query = "";
      // Back to [], not null: MultiSelect's clear writes null, and a null and
      // an empty array are the same thing to the request but not to the
      // template that reads `.length`.
      UTM_FILTERS.forEach((f) => {
        this.filters[f.key] = [];
      });
      this.filters.disbursal_range = [];
      this.reload();
    },
    editLoanStatus(item) {
      // The dialog has no props -- this object is its entire API, and the key
      // names are its own, not the row's.
      this.$refs.editLoanStatusDialog.openDialog({
        loan_id: item.lead_id,
        remark: item.comments,
        disbursement_date: item.disbursal_date,
        disbursed_amount: item.disbursal_amount,
        loan_status: item.status,
        loan_sub_status: item.sub_status,
        show_dialog: true,
      });
    },
    getProductList() {
      let self = this;

      const successHandler = (response) => {
        self.product_list = response.data.result;
      };

      const failureHandler = (error) => {
        console.error("Product list fetch failed", error);
      };

      const params = { filter: true };

      if (self.product_search && self.product_search.trim() !== "") {
        params.search = self.product_search;
      }

      self.request_GET(
        self,
        self.$urls.GET_PRODUCT_LIST,
        params,
        successHandler,
        null,
        failureHandler,
        null
      );
    },

    /**
     * Latest request wins.
     *
     * An exact lead-ID search comes back almost at once, while the unfiltered
     * first load (or a half-typed search) counts across every lead and takes
     * seconds. Without a guard the slow, older answer landed last and replaced
     * the search result -- the "result shows, then vanishes" bug. The previous
     * call is cancelled under the "leads-list" key, and the seq check drops any
     * answer that was already on its way back. A cancelled call still runs
     * finally, so that is gated too, or it would switch the loading bar off
     * under the request that is still running.
     */
    get_lead_list() {
      const self = this;
      const seq = ++self.lead_request_seq;
      const isLatest = () => seq === self.lead_request_seq;
      self.table_loading = true;
      const param = {
        page_number: self.page_number,
        status: self.status,
        search_query: self.search_query,
        type: self.loan_type,
        from_date: this.date_range[0],
        to_date: this.date_range[1],
        // Unset sends nothing: axios drops undefined.
        disbursal_from_date: (self.filters.disbursal_range || [])[0],
        disbursal_to_date: (self.filters.disbursal_range || [])[1],
        page_length: self.page_length,
        product: this.product,
        match_state: self.match_state,
        // Arrays. request_GET uses axios' default serializer, which writes
        // these as `utm_source[]=a&utm_source[]=b` -- the spelling
        // LeadsListView reads via keys.UTM_*_ARRAY. An empty array sends
        // nothing at all.
        ...utmParams(self.filters),
      };
      const successHandler = (response) => {
        if (!isLatest()) return;
        /* A soft failure reaches this handler too -- request_GET calls it even
           when the payload says success:false (see main.js), having already
           shown the error snackbar. Such a payload carries no status_counts, so
           without this the line below replaced every count with a 0 and the
           strip read as an empty screen rather than a failed request. */
        if (response.data.success === false) return;
        self.leads_list = response.data.leads_list;
        self.total_page_count = response.data.total_page_count;
        self.total_count = response.data.total_count;
        self.status_counts = response.data.status_counts || {};
        self.match_counts = response.data.match_counts || {};
        self.unresolved_reported = response.data.unresolved_reported || {};
      };
      const finallyHandler = () => {
        if (isLatest()) self.table_loading = false;
        /* The status list is fetched once, in mounted(). If that request died
           -- an API restart, a 5xx -- the strip would live on its fallback
           labels until a page refresh; asking again on the next list load lets
           it recover by itself. */
        if (!(self.status_list || []).length) self.getLoanStatusList();
      };
      self.request_GET(
        self,
        self.$urls.GET_LEAD_LIST,
        param,
        successHandler,
        null,
        null,
        finallyHandler,
        "leads-list"
      );
    },
    /**
     * The six dropdown lists.
     *
     * The same users/get_utm_dropdown/ every other screen reads, so the values
     * offered here are the values they offer. No failure handler: an
     * unreachable dropdown list should leave the selects empty, not break the
     * page.
     */
    getUtmList() {
      const self = this;
      const successHandler = (response) => {
        // Merged, not assigned: an unexpected shape would otherwise leave
        // utm_list without the six keys the template indexes into.
        self.utm_list = { ...self.utm_list, ...(response.data.result || {}) };
      };
      self.request_GET(self, self.$urls.UTM_LIST, {}, successHandler, null, null, null);
    },
    /** Row -> UTM popup. The rows are raw here, so the row IS the payload. */
    ViewUtm(item) {
      this.$refs.UtmDialog.openDialog(item);
    },
    /**
     * The statuses the strip offers, from the server's own OFFER_LOAN_STATUS
     * choices. Fetched once, in mounted().
     *
     * The failure handler exists to say what happens rather than to do anything:
     * `status_list` is left exactly as it was, because whatever is already there
     * beats an empty list -- an empty one is what used to hide the entire strip.
     * A cold failure has nothing to keep, and statusItems covers that case from
     * the counts instead. request_GET calls this with no arguments, and only
     * when there was no HTTP response at all, so there is nothing to log but the
     * fact of it -- the same shape as getProductList's above.
     */
    getLoanStatusList() {
      let self = this;
      const successHandler = (response) => {
        this.status_list = response.data.result;
      };

      const failureHandler = () => {
        console.error("Loan status list fetch failed");
      };

      self.request_GET(
        self,
        self.$urls.GET_OFFER_STATUS_LIST,
        {},
        successHandler,
        null,
        failureHandler,
        null
      );
    },
    getLoanType() {
      const self = this;

      const params = { flag_active: false };

      const successHandler = (response) => {
        self.loan_type_list = response.data.result;
      };

      self.request_GET(
        self,
        self.$urls.GET_LOAN_TYPE,
        params,
        successHandler,
        null,
        null
      );
    },
    /**
     * Copy a lead's tracking link.
     *
     * Goes through the shared helper rather than navigator.clipboard directly:
     * that object only exists in a SECURE context and this panel is served over
     * plain http, so the unguarded call this used to make threw a TypeError and
     * the button did nothing at all -- not even the error snackbar below.
     */
    async copyOfferLink(url) {
      if (!url) {
        this.showSnakeBar("error", "UTM Link Not Found");
        return;
      }
      const ok = await copyToClipboard(url);
      this.showSnakeBar(
        ok ? "success" : "error",
        ok ? "UTM Link Copied Successfully" : "Failed to copy UTM Link"
      );
    },
  },
};
</script>

<style scoped>
/* The All / Matched / Unmatched dropdown and the reported amount beside it.
   Wraps under the select on a phone. */
.kf-match-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 12px;
}
.kf-match-row__select {
  flex: 0 1 260px;
  max-width: 260px;
}
.kf-match-row__amount {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-align: left;
}
.kf-match-row__amount:hover {
  text-decoration: underline;
}
/* The same two-line cell pair the other list screens use -- a value with a
   quieter fact beneath it. Colour stays #000 on the sub-line: the size drop is
   what separates the two. */
.kf-stack {
  min-width: 0;
  padding: 2px 0;
}
.kf-stack__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Email, product, agent and sub-status are free text and will not always fit.
   Clip with an ellipsis so a long value cannot make one row taller than its
   neighbours; the full value is on the title attribute. */
.kf-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.kf-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

/* Fixed layout, scoped to this screen: the declared widths are a budget that
   sums to the table area, and Vuetify's default auto layout treats them as
   minimums -- one long product name would push the last column off the edge. */
.kf-leads-table >>> .kf-table table {
  table-layout: fixed;
  /* The eleven widths sum to 1554 (was nine summing to 1228), which does not fit the 1132px the table area
     gets on a 1366px laptop. Under table-layout: fixed a container narrower than
     the declared widths scales every column down, so the numbers beside each
     header would quietly stop being the real ones. Stating the sum here makes
     the table scroll sideways inside .kf-table__scroll instead -- and the page
     itself still never scrolls horizontally. */
  min-width: 1554px;
}

/* Marks a row that is a lender's report with no offer behind it yet. */
.kf-tag {
  display: inline-block;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: var(--kf-fs-label);
}
.kf-tag--unmatched {
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fcd34d;
}
/* The borrower applied for the same product again within the hour: a real
   second offer, counted everywhere, tagged so nobody calls them twice
   (leads.repeats on the server). */
.kf-tag--repeat {
  color: #1d4ed8;
  background: #dbeafe;
  border: 1px solid #93c5fd;
}

/* The UTM cell is the click target for the details popup, so it reads as a
   link rather than as plain text sitting in a column. */
.kf-utm {
  color: #00695c;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.kf-utm:hover {
  text-decoration: underline;
}
</style>
